import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, map, of, switchMap, tap, take, shareReplay } from 'rxjs';
import { AuthFlowStatus } from '../../enums/auth-flow-status.enum';
import { ResponseDoctorDto, ResponseMatchingDoctorDto, ResponsePsychoDto, ResponseClinicDto, ResponsePathwayDto, CompletePathwayDto, ResponseVisitDto } from '../../api/models';
import { UserData, QuestionnaireData } from '../../interfaces/auth-flow-state.interface';

/**
 * Auth Flow Service
 * Stateless service following Kosmo pattern - all data in memory
 * Questionnaire data temporarily in sessionStorage during registration only
 */
@Injectable({
  providedIn: 'root'
})
export class AuthFlowService {
  private readonly router = inject(Router);
  
  // ===== STATELESS MANAGEMENT (KOSMO PATTERN) =====
  
  /** Track initialization status */
  private _isInitialized = false;
  private _initializationPromise: Promise<boolean> | null = null;
  
  /** Current step in the flow - in memory only */
  private readonly _currentStepSubject = new BehaviorSubject<AuthFlowStatus>(AuthFlowStatus.QUESTIONNAIRE);
  
  /** User data from backend - in memory only */
  private readonly _userDataSubject = new BehaviorSubject<UserData | null>(null);
  
  /** User role from Cognito - in memory only */
  private readonly _userRoleSubject = new BehaviorSubject<string>('patient');
  
  /** Questionnaire responses - temporarily in sessionStorage during registration */
  private readonly _questionnaireDataSubject = new BehaviorSubject<QuestionnaireData | null>(null);
  
  /** Selected doctor - in memory only */
  private readonly _selectedDoctorSubject = new BehaviorSubject<ResponseMatchingDoctorDto | null>(null);
  
  /** Selected psychologist - in memory only */
  private readonly _selectedPsychoSubject = new BehaviorSubject<ResponsePsychoDto | null>(null);
  
  /** Selected clinic - in memory only */
  private readonly _selectedClinicSubject = new BehaviorSubject<ResponseClinicDto | null>(null);
  
  /** Created pathway - in memory only */
  private readonly _pathwaySubject = new BehaviorSubject<ResponsePathwayDto | CompletePathwayDto | null>(null);
  
  /** First free visit ID for booking - in memory only */
  private readonly _firstVisitIdSubject = new BehaviorSubject<string | null>(null);

  /** Track if user creation polling is in progress - prevents duplicate API calls */
  private readonly _isPollingUserCreationSubject = new BehaviorSubject<boolean>(false);

  /** SessionStorage key for critical state backup */
  private readonly CRITICAL_STATE_BACKUP_KEY = 'authflow_critical_state';

  /** SessionStorage backup expiry: 2 hours (was 24h - too long for sensitive auth data) */
  private readonly BACKUP_EXPIRY_MS = 2 * 60 * 60 * 1000;

  // ===== PUBLIC OBSERVABLES =====
  
  public readonly currentStep$ = this._currentStepSubject.asObservable();
  public readonly userData$ = this._userDataSubject.asObservable();
  public readonly userRole$ = this._userRoleSubject.asObservable();
  public readonly questionnaireData$ = this._questionnaireDataSubject.asObservable();
  public readonly selectedDoctor$ = this._selectedDoctorSubject.asObservable();
  public readonly selectedPsycho$ = this._selectedPsychoSubject.asObservable();
  public readonly selectedClinic$ = this._selectedClinicSubject.asObservable();
  public readonly pathway$ = this._pathwaySubject.asObservable();
  public readonly firstVisitId$ = this._firstVisitIdSubject.asObservable();
  public readonly isPollingUserCreation$ = this._isPollingUserCreationSubject.asObservable();
  
  /** Check if flow is completed */
  public readonly isCompleted$ = this.currentStep$.pipe(
    map(step => {
      // First check if we have the actual COMPLETED status
      if (step === AuthFlowStatus.COMPLETED) {
        return true;
      }
      // Otherwise check if user profile is complete based on actual data
      // This handles the case after page refresh when state is lost
      return this.isUserProfileComplete();
    })
  );
  
  /** Check if user is authenticated */
  public readonly isAuthenticated$ = this.userData$.pipe(
    map(userData => userData !== null)
  );
  
  // Compatibility getters for components using signals
  public get currentStep() { return () => this._currentStepSubject.value; }
  public get userData() { return () => this._userDataSubject.value; }
  public get userRole() { return () => this._userRoleSubject.value; }
  public get questionnaireData() { return () => this._questionnaireDataSubject.value; }
  public get selectedDoctor() { return () => this._selectedDoctorSubject.value; }
  public get selectedPsycho() { return () => this._selectedPsychoSubject.value; }
  public get selectedClinic() { return () => this._selectedClinicSubject.value; }
  public get pathway() { return () => this._pathwaySubject.value; }
  public get firstVisitId() { return () => this._firstVisitIdSubject.value; }
  public get isPollingUserCreation() { return () => this._isPollingUserCreationSubject.value; }
  public get isCompleted() { 
    return () => {
      // First check if we have the actual COMPLETED status
      if (this._currentStepSubject.value === AuthFlowStatus.COMPLETED) {
        return true;
      }
      // Otherwise check if user profile is complete based on actual data
      // This handles the case after page refresh when state is lost
      return this.isUserProfileComplete();
    };
  }
  public get isAuthenticated() { return () => this._userDataSubject.value !== null; }
  
  constructor() {
    // Load questionnaire from sessionStorage if exists (only during registration flow)
    this.loadQuestionnaireFromSession();

    // Restore critical state from sessionStorage backup (userData, userRole, currentStep)
    // This allows users to refresh the page without losing progress
    this._restoreFromBackup();
  }
  
  // ===== FLOW NAVIGATION =====
  
  /**
   * Move to a specific step
   */
  public goToStep(step: AuthFlowStatus): void {
    this._currentStepSubject.next(step);
    // Backup critical state after step change
    this._backupCriticalState();
  }
  
  /**
   * Move to next step in the flow
   * Flow for PATIENTS: QUESTIONNAIRE → REGISTRATION → USER_INFORMATION → DOCTOR_MATCHING →
   *                    PSYCHO_MATCHING → CLINIC_MATCHING → PATHWAY_INITIALIZATION → COMPLETED
   * Flow for PROFESSIONALS (doctor/clinic/psycho): QUESTIONNAIRE → REGISTRATION → USER_INFORMATION → COMPLETED
   * Flow for ADMIN: Any step → COMPLETED
   */
  public goToNextStep(): void {
    const current = this._currentStepSubject.value;
    const role = this._userRoleSubject.value;
    let nextStep: AuthFlowStatus;
    
    // Handle role-specific flows after USER_INFORMATION step
    if (current === AuthFlowStatus.USER_INFORMATION) {
      switch (role) {
        case 'patient':
          // Patients continue with the full flow
          nextStep = AuthFlowStatus.DOCTOR_MATCHING;
          break;
        case 'doctor':
        case 'psycho':
        case 'clinic':
          // Professional users complete after basic info
          nextStep = AuthFlowStatus.COMPLETED;
          break;
        case 'admin':
          // Admins complete immediately
          nextStep = AuthFlowStatus.COMPLETED;
          break;
        default:
          // Unknown role - continue with patient flow as fallback
          nextStep = AuthFlowStatus.DOCTOR_MATCHING;
          break;
      }
      this.goToStep(nextStep);
      return;
    }
    
    // Standard flow progression for all other steps
    switch (current) {
      case AuthFlowStatus.QUESTIONNAIRE:
        nextStep = AuthFlowStatus.DOCTOR_PREVIEW;
        break;
      case AuthFlowStatus.DOCTOR_PREVIEW:
        nextStep = AuthFlowStatus.REGISTRATION;
        break;
      case AuthFlowStatus.REGISTRATION:
        nextStep = AuthFlowStatus.USER_INFORMATION;
        break;
      case AuthFlowStatus.DOCTOR_MATCHING:
        nextStep = AuthFlowStatus.PSYCHO_MATCHING;
        break;
      case AuthFlowStatus.PSYCHO_MATCHING:
        nextStep = AuthFlowStatus.CLINIC_MATCHING;
        break;
      case AuthFlowStatus.CLINIC_MATCHING:
        nextStep = AuthFlowStatus.TEAM_READY;
        break;
      case AuthFlowStatus.TEAM_READY:
        nextStep = AuthFlowStatus.PATHWAY_INITIALIZATION;
        break;
      case AuthFlowStatus.PATHWAY_INITIALIZATION:
        nextStep = AuthFlowStatus.COMPLETED;
        break;
      // FIRST_BOOKING_CONSULTATION step removed - users go directly to home after pathway initialization
      // case AuthFlowStatus.FIRST_BOOKING_CONSULTATION:
      //   nextStep = AuthFlowStatus.COMPLETED;
      //   break;
      default:
        return; // Already completed or unknown state
    }
    
    this.goToStep(nextStep);
  }
  
  /**
   * Go back to a specific step (alias for goToStep for compatibility)
   */
  public goBackToStep(step: AuthFlowStatus): void {
    this.goToStep(step);
  }
  
  /**
   * Navigate directly to login form, bypassing the registration flow
   * Used when user clicks "Login" from questionnaire or doctor preview
   */
  public navigateToLogin(): void {
    this.goToStep(AuthFlowStatus.REGISTRATION);
  }
  
  /**
   * Check if current step can be accessed based on previous steps completion
   */
  public canAccessStep(step: AuthFlowStatus): boolean {
    // Check prerequisites for each step
    switch (step) {
      case AuthFlowStatus.QUESTIONNAIRE:
        return true; // Always accessible
        
      case AuthFlowStatus.DOCTOR_PREVIEW:
        return this._questionnaireDataSubject.value !== null;
        
      case AuthFlowStatus.REGISTRATION:
        return this._questionnaireDataSubject.value !== null;
        
      case AuthFlowStatus.USER_INFORMATION:
        return this._userDataSubject.value !== null;
        
      case AuthFlowStatus.DOCTOR_MATCHING:
        return this._userDataSubject.value !== null && 
               this._questionnaireDataSubject.value !== null &&
               !!this._userDataSubject.value?.firstName;
               
      case AuthFlowStatus.PSYCHO_MATCHING:
        return this._selectedDoctorSubject.value !== null;
        
      case AuthFlowStatus.CLINIC_MATCHING:
        return this._selectedPsychoSubject.value !== null;
        
      case AuthFlowStatus.PATHWAY_INITIALIZATION:
        return this._selectedClinicSubject.value !== null;
        
      // FIRST_BOOKING_CONSULTATION step removed - not accessible anymore
      // case AuthFlowStatus.FIRST_BOOKING_CONSULTATION:
      //   return this._pathwaySubject.value !== null && this._firstVisitIdSubject.value !== null;
        
      case AuthFlowStatus.COMPLETED:
        return this._pathwaySubject.value !== null;
        
      default:
        return false;
    }
  }
  
  // ===== DATA SETTERS =====
  
  /**
   * Save questionnaire data - temporarily in sessionStorage during registration
   */
  public saveQuestionnaireData(data: QuestionnaireData): void {
    this._questionnaireDataSubject.next(data);
    // Save to sessionStorage only for registration flow persistence
    sessionStorage.setItem('temp-questionnaire-data', JSON.stringify(data));
    // Backup critical state
    this._backupCriticalState();
  }
  
  /**
   * Save user data after registration/login - in memory only
   */
  public saveUserData(user: UserData): void {
    this._userDataSubject.next(user);
    // Clear questionnaire from sessionStorage after registration completes
    if (user) {
      sessionStorage.removeItem('temp-questionnaire-data');
    }
    // Backup critical state after user data change
    this._backupCriticalState();
  }
  
  /**
   * Save user role from Cognito - in memory only
   */
  public saveUserRole(role: string): void {
    this._userRoleSubject.next(role);
    // Backup critical state after role change
    this._backupCriticalState();
  }

  /**
   * Set user creation polling flag
   * Used to coordinate between appGuard and registration component
   * Prevents duplicate API calls during user creation polling
   */
  public setPollingUserCreation(isPolling: boolean): void {
    this._isPollingUserCreationSubject.next(isPolling);
  }
  
  /**
   * Save selected doctor - in memory only
   */
  public saveSelectedDoctor(doctor: ResponseMatchingDoctorDto): void {
    this._selectedDoctorSubject.next(doctor);
    // Update user data with doctor ID
    const currentUser = this._userDataSubject.value;
    if (currentUser) {
      this._userDataSubject.next({ ...currentUser, doctorId: doctor.id });
    }
  }
  
  /**
   * Save selected psychologist - in memory only
   */
  public saveSelectedPsycho(psycho: ResponsePsychoDto): void {
    this._selectedPsychoSubject.next(psycho);
    // Update user data with psycho ID
    const currentUser = this._userDataSubject.value;
    if (currentUser) {
      this._userDataSubject.next({ ...currentUser, psychoId: psycho.id });
    }
  }
  
  /**
   * Save selected clinic - in memory only
   */
  public saveSelectedClinic(clinic: ResponseClinicDto): void {
    this._selectedClinicSubject.next(clinic);
    // Update user data with clinic ID
    const currentUser = this._userDataSubject.value;
    if (currentUser) {
      this._userDataSubject.next({ ...currentUser, clinicId: clinic.id });
    }
  }
  
  /**
   * Save pathway data and extract first visit ID - in memory only
   */
  public savePathway(pathway: ResponsePathwayDto | CompletePathwayDto): void {
    this._pathwaySubject.next(pathway);
    
    // Extract first free visit ID for booking - only works with CompletePathwayDto
    this.extractFirstFreeVisitId(pathway);
    
    // Update user data with activePathway flag
    const currentUser = this._userDataSubject.value;
    if (currentUser) {
      this._userDataSubject.next({ ...currentUser, activePathway: true });
    }
    
    // Clear questionnaire from sessionStorage after pathway creation
    sessionStorage.removeItem('temp-questionnaire-data');
  }
  
  // ===== FLOW CONTROL =====
  
  /**
   * Complete the current step and move to next
   */
  public completeCurrentStep(): void {
    this.goToNextStep();
  }
  
  /**
   * Get current status (alias for currentStep for compatibility)
   */
  public get currentStatus() { return this.currentStep; }
  
  /**
   * Force set status (alias for goToStep for compatibility)
   */
  public forceStatus(status: AuthFlowStatus): void {
    this.goToStep(status);
  }
  
  /**
   * Reset the entire flow - clears all in-memory data
   */
  public resetFlow(): void {
    this._currentStepSubject.next(AuthFlowStatus.QUESTIONNAIRE);
    this._userDataSubject.next(null);
    this._userRoleSubject.next('patient');
    this._questionnaireDataSubject.next(null);
    this._selectedDoctorSubject.next(null);
    this._selectedPsychoSubject.next(null);
    this._selectedClinicSubject.next(null);
    this._pathwaySubject.next(null);
    this._firstVisitIdSubject.next(null);
    this._isPollingUserCreationSubject.next(false);

    // Clear sessionStorage
    sessionStorage.removeItem('temp-questionnaire-data');
    sessionStorage.removeItem(this.CRITICAL_STATE_BACKUP_KEY);
  }
  
  /**
   * Clear selections but keep user logged in - clears in-memory selections only
   */
  public clearSelections(): void {
    this._questionnaireDataSubject.next(null);
    this._selectedDoctorSubject.next(null);
    this._selectedPsychoSubject.next(null);
    this._selectedClinicSubject.next(null);
    this._pathwaySubject.next(null);
    this._firstVisitIdSubject.next(null);
    this._currentStepSubject.next(AuthFlowStatus.QUESTIONNAIRE);

    // Clear sessionStorage
    sessionStorage.removeItem('temp-questionnaire-data');

    // Backup updated state (user still logged in, but selections cleared)
    this._backupCriticalState();
  }
  
  // ===== SESSION STORAGE FOR QUESTIONNAIRE ONLY =====
  
  /**
   * Load questionnaire from sessionStorage if exists
   * Only used during registration flow to persist questionnaire data
   */
  private loadQuestionnaireFromSession(): void {
    try {
      const stored = sessionStorage.getItem('temp-questionnaire-data');
      if (stored) {
        const data = JSON.parse(stored);
        this._questionnaireDataSubject.next(data);
      }
    } catch (error) {
      // Silently fail if sessionStorage is corrupted
      sessionStorage.removeItem('temp-questionnaire-data');
    }
  }

  /**
   * Backup critical state to sessionStorage
   * Called automatically when userData, userRole, or currentStep changes
   * Prevents data loss on page refresh during registration
   *
   * ✅ CRITICAL FIX #4: Only backup primitive/simple data types
   * ✅ CRITICAL FIX #8: Handle sessionStorage quota exceeded gracefully
   * Complex objects (userData with methods, dates, circular refs) are NOT backed up to avoid:
   * - JSON serialization issues
   * - SessionStorage quota exceeded
   * - Type information loss
   *
   * After page refresh, components will call loadUserData() to fetch fresh userData
   */
  private _backupCriticalState(): void {
    try {
      const backup = {
        // ❌ REMOVED: userData (complex object - will be loaded fresh on page refresh)
        userRole: this._userRoleSubject.value,           // string primitive
        currentStep: this._currentStepSubject.value,     // enum/string primitive
        questionnaireData: this._questionnaireDataSubject.value,  // plain object from form
        timestamp: Date.now()                            // number primitive
      };

      sessionStorage.setItem(this.CRITICAL_STATE_BACKUP_KEY, JSON.stringify(backup));
      console.log('[AuthFlowService] Critical state backed up (primitives only)');
    } catch (error) {
      console.warn('[AuthFlowService] Failed to backup state to sessionStorage:', error);

      // ✅ CRITICAL FIX #8: Handle quota exceeded by clearing old data
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.error('[AuthFlowService] SessionStorage quota exceeded - attempting cleanup');
        try {
          // Try to clear temp questionnaire data and retry
          sessionStorage.removeItem('temp-questionnaire-data');

          // Retry with minimal backup (no questionnaire data)
          const minimalBackup = {
            userRole: this._userRoleSubject.value,
            currentStep: this._currentStepSubject.value,
            timestamp: Date.now()
          };
          sessionStorage.setItem(this.CRITICAL_STATE_BACKUP_KEY, JSON.stringify(minimalBackup));
          console.log('[AuthFlowService] Minimal backup saved after quota cleanup');
        } catch (retryError) {
          console.error('[AuthFlowService] Still failed after cleanup - sessionStorage full');
          // Don't throw - backup is best-effort, not critical
        }
      }
    }
  }

  /**
   * Restore critical state from sessionStorage backup
   * Called on service initialization (page refresh/reload)
   * Validates timestamp to prevent stale data (2h expiry)
   *
   * ✅ CRITICAL FIX #4: Only restore primitive/simple data types
   * ✅ CRITICAL FIX #12: Validate restored state structure
   * userData is NOT restored - components will call loadUserData() to fetch fresh data
   */
  private _restoreFromBackup(): void {
    try {
      const stored = sessionStorage.getItem(this.CRITICAL_STATE_BACKUP_KEY);
      if (!stored) {
        return; // No backup found
      }

      const backup = JSON.parse(stored);

      // ✅ CRITICAL FIX #12: Validate backup structure
      if (!backup || typeof backup !== 'object') {
        console.error('[AuthFlowService] Invalid backup structure');
        sessionStorage.removeItem(this.CRITICAL_STATE_BACKUP_KEY);
        return;
      }

      // ✅ CRITICAL FIX #12: Validate timestamp
      if (typeof backup.timestamp !== 'number' || backup.timestamp <= 0) {
        console.error('[AuthFlowService] Invalid timestamp in backup');
        sessionStorage.removeItem(this.CRITICAL_STATE_BACKUP_KEY);
        return;
      }

      // Validate timestamp - expire after 2 hours (reduced from 24h)
      if (Date.now() - backup.timestamp > this.BACKUP_EXPIRY_MS) {
        console.log('[AuthFlowService] Backup expired (>2h), clearing...');
        sessionStorage.removeItem(this.CRITICAL_STATE_BACKUP_KEY);
        return;
      }

      // ✅ Restore only primitive state (no complex objects)
      // ❌ REMOVED: userData restore (complex object - will be loaded fresh)

      // ✅ CRITICAL FIX #12: Validate userRole before restoring
      if (backup.userRole && typeof backup.userRole === 'string') {
        this._userRoleSubject.next(backup.userRole);
        console.log('[AuthFlowService] Restored userRole from backup');
      }

      // ✅ CRITICAL FIX #12: Validate currentStep is valid enum value
      // ✅ CRITICAL FIX #13: Don't restore COMPLETED state without userData
      // This prevents UI getting stuck in "Accesso completato" after logout
      if (backup.currentStep) {
        const validSteps = Object.values(AuthFlowStatus);
        if (validSteps.includes(backup.currentStep)) {
          // Don't restore COMPLETED state if we don't have user data
          // After logout, user data is cleared but backup might still exist
          if (backup.currentStep === AuthFlowStatus.COMPLETED && !this._userDataSubject.value) {
            // Reset to initial state instead - likely stale state after logout
            this._currentStepSubject.next(AuthFlowStatus.QUESTIONNAIRE);
          } else {
            this._currentStepSubject.next(backup.currentStep);
          }
        }
      }

      // ✅ CRITICAL FIX #12: Validate questionnaireData structure
      if (backup.questionnaireData && typeof backup.questionnaireData === 'object') {
        this._questionnaireDataSubject.next(backup.questionnaireData);
        console.log('[AuthFlowService] Restored questionnaireData from backup');
      }

      console.log('[AuthFlowService] Critical state restored (primitives only)');
    } catch (error) {
      console.warn('[AuthFlowService] Failed to restore state from sessionStorage:', error);
      // Clear corrupted backup
      sessionStorage.removeItem(this.CRITICAL_STATE_BACKUP_KEY);
    }
  }
  
  // ===== HELPERS =====
  
  /**
   * Check if user profile is fully complete
   * For patients: needs firstName, lastName, doctorId, psychoId, clinicId, activePathway
   * For clinics: needs name, street, and other relevant fields
   * For doctors/psycho: needs firstName, lastName
   */
  public isUserProfileComplete(): boolean {
    const user = this._userDataSubject.value;
    if (!user) return false;
    
    const role = this._userRoleSubject.value;
    
    switch (role) {
      case 'patient':
        // Patient needs all fields to be complete
        return !!(
          user.firstName &&
          user.lastName &&
          user.doctorId &&
          user.psychoId &&
          user.clinicId &&
          user.activePathway
        );
        
      case 'clinic':
        // Clinic needs name and address
        return !!(user.name && user.street);
        
      case 'doctor':
      case 'psycho':
        // Doctors/psycho just need basic info
        return !!(user.firstName && user.lastName);
        
      default:
        return false;
    }
  }
  
  /**
   * Check if user has basic information
   * For patients/doctors/psycho: firstName and lastName
   * For clinics: name and street
   */
  public hasBasicUserInfo(): boolean {
    const user = this._userDataSubject.value;
    if (!user) return false;
    
    const role = this._userRoleSubject.value;
    
    if (role === 'clinic') {
      return !!(user.name && user.street);
    }
    
    return !!(user.firstName && user.lastName);
  }
  
  /**
   * Determine the appropriate step based on user data completeness
   * Called after fetching user data to skip completed steps
   */
  public determineAppropriateStep(): AuthFlowStatus {
    const user = this._userDataSubject.value;
    const role = this._userRoleSubject.value;
    
    if (!user) return AuthFlowStatus.USER_INFORMATION;
    
    // Different logic based on user role
    switch (role) {
      case 'patient':
        // Patients need to complete the full registration flow
        if (this.isUserProfileComplete()) {
          return AuthFlowStatus.COMPLETED;
        }
        
        if (!this.hasBasicUserInfo()) {
          return AuthFlowStatus.USER_INFORMATION;
        }
        
        if (!user.doctorId) {
          return AuthFlowStatus.DOCTOR_MATCHING;
        }
        
        if (!user.psychoId) {
          return AuthFlowStatus.PSYCHO_MATCHING;
        }
        
        if (!user.clinicId) {
          return AuthFlowStatus.CLINIC_MATCHING;
        }
        
        if (!user.activePathway) {
          return AuthFlowStatus.PATHWAY_INITIALIZATION;
        }
        
        return AuthFlowStatus.COMPLETED;
        
      case 'doctor':
      case 'psycho':
      case 'clinic':
        // Professional users only need basic info
        if (this.hasBasicUserInfo()) {
          return AuthFlowStatus.COMPLETED;
        }
        return AuthFlowStatus.USER_INFORMATION;
        
      case 'admin':
        // Admins are always considered complete
        return AuthFlowStatus.COMPLETED;
        
      default:
        // Default to user information for unknown roles
        return AuthFlowStatus.USER_INFORMATION;
    }
  }
  
  /**
   * Get completion route based on user role
   */
  public getCompletionRoute(): string {
    const role = this._userRoleSubject.value;
    switch (role) {
      case 'patient':
        return '/home';
      case 'doctor':
      case 'psycho':
        return '/my-patients';
      case 'clinic':
        return '/pathways-management';
      case 'admin':
        return '/admin';
      default:
        return '/home';
    }
  }
  
  /**
   * Check if a required step is completed
   */
  public isStepCompleted(step: AuthFlowStatus): boolean {
    const currentStepIndex = this.getStepIndex(this._currentStepSubject.value);
    const targetStepIndex = this.getStepIndex(step);
    
    return currentStepIndex > targetStepIndex;
  }
  
  /**
   * Get numeric index of a step for comparison
   */
  private getStepIndex(step: AuthFlowStatus): number {
    const steps = [
      AuthFlowStatus.QUESTIONNAIRE,
      AuthFlowStatus.DOCTOR_PREVIEW,
      AuthFlowStatus.REGISTRATION,
      AuthFlowStatus.USER_INFORMATION,
      AuthFlowStatus.DOCTOR_MATCHING,
      AuthFlowStatus.PSYCHO_MATCHING,
      AuthFlowStatus.CLINIC_MATCHING,
      AuthFlowStatus.TEAM_READY,
      AuthFlowStatus.PATHWAY_INITIALIZATION,
      // AuthFlowStatus.FIRST_BOOKING_CONSULTATION, // Removed step
      AuthFlowStatus.COMPLETED
    ];

    return steps.indexOf(step);
  }
  
  /**
   * Helper method to extract first free visit ID from pathway
   */
  private extractFirstFreeVisitId(pathway: ResponsePathwayDto | CompletePathwayDto): void {
    try {
      // This only works with CompletePathwayDto which has ResponseVisitDto visits
      const completePathway = pathway as CompletePathwayDto;
      const firstPhase = completePathway.phases?.[0];
      
      if (firstPhase?.visits) {
        const visits = firstPhase.visits as ResponseVisitDto[];
        const firstFreeVisit = visits.find(visit => visit.isFree === true);
        
        if (firstFreeVisit?.id) {
          this._firstVisitIdSubject.next(firstFreeVisit.id);
        }
      }
    } catch (error) {
      // Silently fail if pathway doesn't have the expected structure
    }
  }
}